import ExpensesCodeModal from '@/components/program/ExpensesCodeModal';
import { HeaderWithSubtitle } from '@/components/program/Header';
import LocationModal from '@/components/program/LocationModal';
import RideTypeModal from '@/components/program/RideTypeModal';
import TimeModal from '@/components/program/TimeModal';
import { GROUPS } from '@/constants/group';
import { TRAVEL_RIDE_TYPE, TRAVEL_TIME_TYPE } from '@/constants/travel-program';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaCar, FaClock } from 'react-icons/fa6';
import { MdPinDrop, MdReceiptLong } from 'react-icons/md';
import AddTravelProgramForm from '../components/AddTravelProgramForm';
import { travelProgramInitialValues, travelProgramSchema } from '../engine/travel-program.model';
import { cn } from '@/components/ui/cn';
import { createTypedModal, useTypedModal } from '@/common/common_utils';
import { Button } from 'primereact/button';
import { BackButton } from '@/components/buttons/Buttons';
import { Column } from '@/components/container/Container';
import { TextBodyLarge, TextBodyMedium, TextHeadlineLarge, TextHeadlineMedium } from '@/components/text/Texts';

const AddTravelProgramModal = createTypedModal<{}, undefined>(() => {
	const modal = useTypedModal();

	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [showExpensesCodeModal, setShowExpensesCodeModal] = useState(false);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [showTimeModal, setShowTimeCodeModal] = useState(false);
	const [showRideTypeModal, setShowRideTypeModal] = useState(false);

	const form = useForm({
		defaultValues: travelProgramInitialValues,
		resolver: yupResolver(travelProgramSchema),
	});

	const { watch } = form;

	const programName = watch('name');
	const locationTypeValue = watch('locationType');
	const expenseCodeTypeValue = watch('expenseCodeType');
	const rideTypeValue = watch('rideType');
	const ridesValue = watch('rides');
	const timeTypeValue = watch('timeType');

	const isNextDisabled = !programName || programName.trim() === '';

	const renderTimeRuleValue = () => {
		if (timeTypeValue === TRAVEL_TIME_TYPE.custom) return 'Custom time';
		if (timeTypeValue === TRAVEL_TIME_TYPE['all-day']) return 'All day (24 hours)';
		return 'Weekdays';
	};

	const rules = [
		{
			icon: <MdReceiptLong className="text-primary" size={24} />,
			title: 'Full coverage of trip expenses.',
			value: expenseCodeTypeValue === 'required'
				? 'Expense code required.'
				: 'No expense code required.',
			onClick: () => setShowExpensesCodeModal(true),
		},
		{
			icon: <MdPinDrop className="text-primary" size={24} />,
			title: 'Locations',
			value: locationTypeValue === 'any'
				? 'Any location'
				: 'Limited locations',
			onClick: () => setShowLocationModal(true),
		},
		{
			icon: <FaClock className="text-primary" size={24} />,
			title: 'Time',
			value: renderTimeRuleValue(),
			onClick: () => setShowTimeCodeModal(true),
		},
		{
			icon: <FaCar className="text-primary" size={24} />,
			title: 'Ride type',
			value: rideTypeValue === TRAVEL_RIDE_TYPE.all
					? 'All rides'
					: `Limited Ride types (${ridesValue?.length} selected)`,
			onClick: () => setShowRideTypeModal(true),
		},
	];

	const onNext = () => setActiveIndex((prev) => prev + 1);
	const onBack = () => {
		if (activeIndex === 0) modal.resolveRemove();
		else setActiveIndex((prev) => prev - 1);
	};

	return (
		<FormProvider {...form}>
			<Dialog
				unstyled
				visible={true}
				onHide={() => modal.resolveRemove()}
				draggable={false}
				closable={false}
				maskStyle={{ backgroundColor: '#00000047' }}
				pt={{
					root: { className: 'p-8 rounded-[28px] w-[732px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)] bg-white' },
					content: { className: 'flex flex-col p-0' },
				}}
			>
				<BackButton
					withIcon
					id="back-add-travel-program"
					label="Back"
					onClick={onBack}
				/>
				<TextBodyLarge className="mt-3 mb-1 text-tertiary" text="Travel Program" />
				<TextHeadlineLarge
					className="mb-2 text-neutral17"
					text={activeIndex === 0 ? 'Create a Program' : 'Customize your program'}
				/>
				<TextBodyMedium className="mb-3 text-neutral17" text="You can always change these settings later." />
				<TextHeadlineMedium className="mb-6 text-neutral17" text="Program name" />
				<div className="mb-5">
					<AddTravelProgramForm
						activeIndex={activeIndex}
						groups={GROUPS}
						rules={rules}
					/>
				</div>
				<Button
					label={activeIndex === 0 ? 'Next' : 'Save'}
					disabled={isNextDisabled}
					className={cn(
						'self-end h-12 w-[112px] rounded-[100px]',
						!isNextDisabled ? 'bg-primary text-white' : 'bg-[#D2D5D7] text-[#8A8C8D] pointer-events-none',
					)}
					onClick={() => {
						if (activeIndex === 0) onNext()
						else {
							modal.resolveRemove();
							showNotification('Travel Program successfully created.');
						}
					}}
				/>
			</Dialog>

			<ExpensesCodeModal
				visible={showExpensesCodeModal}
				setVisible={setShowExpensesCodeModal}
				onHide={() => setShowExpensesCodeModal(false)}
			/>
			<LocationModal
				visible={showLocationModal}
				setVisible={setShowLocationModal}
				onHide={() => setShowLocationModal(false)}
			/>
			<TimeModal
				visible={showTimeModal}
				setVisible={setShowTimeCodeModal}
				onHide={() => setShowTimeCodeModal(false)}
			/>
			<RideTypeModal
				visible={showRideTypeModal}
				setVisible={setShowRideTypeModal}
				onHide={() => setShowRideTypeModal(false)}
			/>
		</FormProvider>
	);
});

export default AddTravelProgramModal;
